import { Fragment } from "react";
import type { DeviceStatus, GeneralSettings, Order } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { readyTime } from "../../lib/ready-time";
import { formatDate, formatTime } from "../../lib/time";
import { QrCode } from "../../ui/QrCode";
import { groupHours, shopAddress } from "../general/general";
import { describeModifier, noteText } from "../sale/modifiers";
import { streetLine } from "./order-view";

interface Props {
  order: Order;
  shop: DeviceStatus["shop"];
  /** Algemeen: name, address, hours, header and footer; the onboarding details until loaded. */
  general: GeneralSettings | null;
  /** The paper of the receipt printer: 80 mm, or 58 mm on a Sunmi (prototype receiptTarget). */
  paper?: 58 | 80;
}

/**
 * The top of every receipt (prototype bonHeadHtml): the name on the receipt, the company, address,
 * phone, VAT number and extra lines, then the opening hours when the shop shows them.
 */
export function ReceiptHead({ shop, general }: { shop: DeviceStatus["shop"]; general: GeneralSettings | null }) {
  if (general === null) {
    const firm = shop.firm;
    return (
      <header className="c">
        <div className="shop">{shop.name.toUpperCase()}</div>
        {firm && (
          <>
            <div>{firm.name}</div>
            {firm.address !== null && <div>{firm.address}</div>}
            {firm.vatNumber !== null && <div>{t.receipt.vatNumber(firm.vatNumber)}</div>}
          </>
        )}
      </header>
    );
  }
  return (
    <>
      <header className="c">
        <div className="shop">{general.receiptName}</div>
        {general.company && <div>{general.company}</div>}
        <div>{shopAddress(general)}</div>
        {general.phone && <div>{t.receipt.tel(general.phone)}</div>}
        {general.vatNumber && <div>{t.receipt.vatNumber(general.vatNumber)}</div>}
        {general.headerLines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </header>
      {general.showHours && (
        <div className="c blk">
          <b>{t.receipt.hours}</b>
          {groupHours(general.openingHours).map(([days, hours]) => (
            <div className="muted" key={days}>
              {days}: {hours}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** The bottom of every receipt: the shop's own lines and, small, "Kassa door Tafo · tafo.be". */
export function ReceiptFoot({ general }: { general: GeneralSettings | null }) {
  const lines = general ? general.footerLines : [t.receipt.thanks];
  return (
    <footer>
      {lines.length > 0 && (
        <div className="c blk">
          {lines.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
      <div className="bontafo">
        <img src="/tafo-logo.png" alt="Tafo" />
        <span>{t.receipt.tafo}</span>
      </div>
    </footer>
  );
}

/** The VAT letters of the prototype and Donass: A 21%, B 12%, C 6%, D 0%. */
export const VAT_LETTERS: Record<number, string> = { 21: "A", 12: "B", 6: "C", 0: "D" };

/** "30-09-2026" (prototype: the date with dashes). */
const receiptDate = (iso: string) => formatDate(iso).replace(/\//g, "-");
/** "19.42" (prototype: the time of the order with a dot). */
const receiptTime = (iso: string) => formatTime(iso).replace(":", ".");

/**
 * The route to a delivery address in Google Maps (prototype mapsUrl), for the QR on the receipt: the
 * courier scans it and drives, also on an iPhone.
 */
export function routeUrl(customer: Record<string, string>): string {
  const { street = "", number = "", postcode = "", zone = "" } = customer;
  const address = [`${street} ${number}`.trim(), `${postcode} ${zone}`.trim(), "België"].filter(Boolean).join(", ");
  return `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${encodeURIComponent(address)}`;
}

/**
 * The VAT table of a receipt (prototype, Donass): per rate the letter, rate, base, VAT and total, and a
 * line with the sums when there is more than one rate; on 58 mm only rate, base and VAT.
 */
export function VatTable({ vat, narrow }: { vat: Order["vat"]; narrow: boolean }) {
  const text = t.receipt;
  const vatRows = [...vat].sort((a, b) => a.rate - b.rate);
  const sum = (pick: (row: (typeof vatRows)[number]) => number) => vatRows.reduce((total, row) => total + pick(row), 0);
  return narrow ? (
    <table>
      <thead>
        <tr>
          <th>{text.vat}</th>
          <th>{text.base}</th>
          <th>{text.vat}</th>
        </tr>
      </thead>
      <tbody>
        {vatRows.map((row) => (
          <tr key={row.rate}>
            <td>
              {VAT_LETTERS[row.rate]} {row.rate}%
            </td>
            <td>{formatEuro(row.baseCents)}</td>
            <td>{formatEuro(row.vatCents)}</td>
          </tr>
        ))}
        {vatRows.length > 1 && (
          <tr>
            <td />
            <td>{formatEuro(sum((row) => row.baseCents))}</td>
            <td>{formatEuro(sum((row) => row.vatCents))}</td>
          </tr>
        )}
      </tbody>
    </table>
  ) : (
    <table>
      <thead>
        <tr>
          <th />
          <th>{text.rate}</th>
          <th>{text.base}</th>
          <th>{text.vat}</th>
          <th>{text.total}</th>
        </tr>
      </thead>
      <tbody>
        {vatRows.map((row) => (
          <tr key={row.rate}>
            <td>{VAT_LETTERS[row.rate]}</td>
            <td>{row.rate}%</td>
            <td>{formatEuro(row.baseCents)}</td>
            <td>{formatEuro(row.vatCents)}</td>
            <td>{formatEuro(row.grossCents)}</td>
          </tr>
        ))}
        {vatRows.length > 1 && (
          <tr>
            <td />
            <td />
            <td>{formatEuro(sum((row) => row.baseCents))}</td>
            <td>{formatEuro(sum((row) => row.vatCents))}</td>
            <td>{formatEuro(sum((row) => row.grossCents))}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/**
 * The customer receipt as it is printed (prototype receiptParts, "bon zoals Donass"), shown next to
 * the order list. The same element is what "Afdrukken" prints from the browser; the Windows layer will
 * send it to the receipt printer.
 */
/**
 * A printed line that opens its category: it has a category and the line above it another one (or none). Only
 * printed orders carry a category, so the till's own screens never show these lines.
 */
export function startsCategory(lines: readonly { category?: string | null }[], index: number): boolean {
  const category = lines[index]?.category;
  if (!category) return false;
  return index === 0 || lines[index - 1]?.category !== category;
}

/**
 * Takeaway.com and Uber Eats hide the customer's number behind their own exchange: the code to give after dialling
 * it, under the number (Furkan, 29/10/2026). Not when the note says it already (JET Connect writes it there).
 */
export function phoneCodeLine(order: Order): string | null {
  const { phone, phoneCode } = order.customer;
  if (!phone || !phoneCode) return null;
  const line = t.receipt.phoneCode(phoneCode);
  return order.note?.includes(line) ? null : line;
}

export function Receipt({ order, shop, general, paper = 80 }: Props) {
  const text = t.receipt;
  const payments = order.payments.filter((payment) => payment.kind === "payment");
  const refunds = order.payments.filter((payment) => payment.kind === "refund");
  const due = order.totalCents - order.paidCents;
  const customer = order.customer;
  const cancelled = order.status === "cancelled";
  const delivery = order.channel === "delivery";
  const codeLine = phoneCodeLine(order);
  const narrow = paper === 58;
  const methods = [...new Set(payments.map((payment) => payment.method))];
  const change = payments.reduce((sum, payment) => sum + payment.changeCents, 0);
  const manualCents = order.discountCents - order.promotionCents - order.rewardCents;

  return (
    <article
      className={`bon${narrow ? " w58" : ""}${cancelled ? " void" : ""}`}
      aria-label={`${t.orders.receipt} ${order.code}`}
    >
      <ReceiptHead shop={shop} general={general} />

      {cancelled && (
        // printed too: nobody can mistake this receipt for a sale
        <div className="void-stamp" role="note">
          <b>{text.cancelled}</b>
          {order.cancelledAt !== null && (
            <span>
              {formatDate(order.cancelledAt)} {formatTime(order.cancelledAt)}
            </span>
          )}
          {order.cancelReason !== null && <span>{order.cancelReason}</span>}
        </div>
      )}

      <section className="blk">
        <div className="kv">
          <span>{text.kind}</span>
          <span>
            {t.sale.channels[order.channel]}
            {order.table !== null && ` · ${t.orders.table(order.table)}`}
          </span>
        </div>
        <div className="kv">
          <span>{text.number}</span>
          <span className="code5">{order.code}</span>
        </div>
        <div className="kv">
          <span>{text.date}</span>
          <span>{receiptDate(order.createdAt)}</span>
        </div>
        <div className="kv">
          <span>{text.time}</span>
          <span>{receiptTime(order.createdAt)}</span>
        </div>
        {methods.length > 0 && (
          <div className="kv">
            <span>{text.method}</span>
            <span>{methods.map((method) => text.methods[method] ?? t.paymentMethods[method]).join(" + ")}</span>
          </div>
        )}
      </section>
      {order.channel !== "table" && general && (
        // ready at: the order's time plus the preparation time of Algemeen, in bold (prototype); at the
        // latest the closing (Furkan, 11/10/2026)
        <div className="kv blk strong">
          <span>{delivery ? text.deliveryTime : text.pickup}</span>
          <span>
            {formatTime(readyTime(new Date(order.createdAt), general.prepMinutes[order.channel], general.openingHours))}
          </span>
        </div>
      )}

      {delivery ? (
        // prototype: the address, phone, "Dhr. / Mvr." and the name, the note; the route as a QR code
        <div className="blk addr">
          <div>
            <b>{text.deliveryAddress}</b>
            {streetLine(customer) && <div>{streetLine(customer)}</div>}
            {customer.zone && <div>{[customer.postcode, customer.zone].filter(Boolean).join(" ")}</div>}
            {customer.phone && <div>{customer.phone}</div>}
            {codeLine && <div>{codeLine}</div>}
            <div className="salute">
              {text.salutation}
              <br />
              {customer.name ?? ""}
            </div>
            {order.note !== null && (
              <div className="remark">
                <b>{text.remark}</b> {order.note}
              </div>
            )}
          </div>
          {customer.street && (
            <div className="bqr">
              <QrCode text={routeUrl(customer)} label={text.route} />
            </div>
          )}
        </div>
      ) : (
        <>
          {customer.name || customer.phone ? (
            // the phone also without a name: an order by phone to pick up (Antalya, 29/10/2026)
            <div className="blk">
              {customer.name && (
                <>
                  {text.salutation}
                  <br />
                  {customer.name}
                  {customer.phone && <br />}
                </>
              )}
              {customer.phone}
              {codeLine && (
                <>
                  <br />
                  {codeLine}
                </>
              )}
            </div>
          ) : null}
          {order.note !== null && (
            <div className="blk">
              <b>{text.remark}</b> {order.note}
            </div>
          )}
        </>
      )}

      <h4>{text.details}</h4>
      {order.lines.map((line, index) => (
        <Fragment key={index}>
          {startsCategory(order.lines, index) && <div className="cat">{line.category}</div>}
          <div className="it">
            <span>{line.quantity}x</span>
            {/* "Naam op de bon" (Menu & prijzen) when the shop set one */}
            <span>
              {line.receiptName ?? line.name}
              {line.variant !== null && ` ${line.variant}`}
            </span>
            <span className="pr">
              {line.free ? text.free : formatEuro(line.lineCents)} {VAT_LETTERS[line.vatRate]}
            </span>
            {/* extras without a price (decisions: "Extra's op de bon zonder prijs") */}
            {line.modifiers.map((modifier, modifierIndex) => (
              <span className="mod" key={modifierIndex}>
                {describeModifier(modifier, false)}
              </span>
            ))}
            {line.note !== null && <span className="mod">{noteText(line.note)}</span>}
          </div>
        </Fragment>
      ))}
      {order.deliveryCents > 0 && (
        <div className="kv blk">
          <span>{t.delivery.receipt}</span>
          <span>
            {formatEuro(order.deliveryCents)} {VAT_LETTERS[6]}
          </span>
        </div>
      )}
      {order.promotionCents > 0 && (
        <div className="kv">
          <span>{t.promotions.receiptLine}</span>
          <span>−{formatEuro(order.promotionCents)}</span>
        </div>
      )}
      {order.rewardCents > 0 && (
        <div className="kv">
          <span>{t.loyalty.line}</span>
          <span>−{formatEuro(order.rewardCents)}</span>
        </div>
      )}
      {manualCents > 0 && (
        <div className="kv">
          <span>{t.sale.discount}</span>
          <span>−{formatEuro(manualCents)}</span>
        </div>
      )}

      <div className="rule" />
      <VatTable vat={order.vat} narrow={narrow} />

      <div className="total">
        <span>{t.sale.total}</span>
        <span>{formatEuro(order.totalCents)}</span>
      </div>
      {order.tipCents > 0 && (
        <>
          <div className="kv">
            <span>{text.tip}</span>
            <span>{formatEuro(order.tipCents)}</span>
          </div>
          <div className="kv blk">
            <span>{text.paidTotal}</span>
            <span>{formatEuro(order.paidCents + order.tipCents)}</span>
          </div>
        </>
      )}

      {/* "REEDS BETAALD!" only on a delivery, for the courier: a takeaway, a table and what the Tafo app
          prints are paid where they are handed over (Furkan, 11/10/2026) */}
      {!(order.channel !== "delivery" && !cancelled && due <= 0) && (
        <div className="c paid">
          {cancelled
            ? text.cancelled
            : due <= 0
              ? text.paid
              : delivery
                ? text.payCourier(formatEuro(due))
                : text.toPay(formatEuro(due))}
        </div>
      )}
      {!cancelled && due > 0 && order.receiptQr?.amountCents === due && <ReceiptQrBlock qr={order.receiptQr} />}
      {change > 0 && <div className="c">{text.change(formatEuro(change))}</div>}
      {refunds.map((refund) => (
        <div className="c" key={refund.id}>
          {text.refunded(formatEuro(-refund.amountCents), t.paymentMethods[refund.method])}
        </div>
      ))}
      <ReceiptFoot general={general} />
    </article>
  );
}

/**
 * The Bancontact Pro QR at the bottom of a receipt or a bill (ADR 0029): the customer scans it with the app
 * of his bank, at the counter or at the door; Tafo books the payment when Bancontact confirms it.
 */
export function ReceiptQrBlock({ qr }: { qr: NonNullable<Order["receiptQr"]> }) {
  const text = t.checkout.receiptQr;
  return (
    <div className="c blk payqr">
      <b>{text.onReceipt}</b>
      <div className="payqr-code">
        <QrCode text={qr.text} label={text.onReceipt} />
      </div>
      <div>{text.scan(formatEuro(qr.amountCents))}</div>
    </div>
  );
}
